import type { AppRouteRecordRaw } from "../types/types";

import accountHealthRoutes from "@/app/router/accountHealth";
import adminRoutes from "@/app/router/admin";
import advertisingRoutes from "@/app/router/advertising";
import categoryRoutes from "@/app/router/category";
import productRatingAndReviewsRoutes from "@/app/router/productRatingAndReviews";
import { RouteNames } from "@/app/router/types/names";
import { Permissions } from "@/shared/constants";

const coreRoutes: AppRouteRecordRaw[] = [
  {
    path: "/insightful",
    name: RouteNames.SOINSIGHTFUL,
    component: () => import("@/pages/InsightFul.vue"),
    meta: {
      title: "SO Insightful",
      menuIcon: "lucide:lightbulb",
      menuTitle: "Insightful",
      permissions: [Permissions.INSIGHTFUL],
    },
  },
  {
    path: "/sales-and-traffic",
    name: RouteNames.SALES_AND_TRAFFIC,
    component: () => import("@/pages/SalesAndTraffic.vue"),
    meta: {
      title: "Sales and Traffic",
      menuIcon: "lucide:circle-dollar-sign",
      permissions: [Permissions.SALES_REPORT],
      vendorPermissions: [Permissions.READ_VENDOR_REPORT_SALES],
      partnerships: ["seller", "vendor"],
    },
  },
  {
    path: "/product-returns",
    name: RouteNames.PRODUCT_RETURNS,
    component: () => import("@/pages/ProductReturns.vue"),
    meta: {
      title: "Product Returns",
      menuIcon: "lucide:package-x",
      permissions: [Permissions.RETURNS],
    },
  },
  ...advertisingRoutes,
  ...productRatingAndReviewsRoutes,
  {
    path: "/inventory",
    name: RouteNames.INVENTORY,
    meta: {
      filters: {
        show: ["brands", "channels", "products"],
      },
      title: "Inventory",
      menuIcon: "lucide:boxes",
      permissions: [Permissions.INVENTORY],
    },
    component: () => import("@/pages/InventoryReview.vue"),
  },
  ...categoryRoutes,
  {
    path: "/product-performance",
    name: RouteNames.PERFORMANCE,
    component: () => import("@/pages/ProductPerformance.vue"),
    meta: {
      title: "Product Performance",
      menuIcon: "lucide:trending-up",
      permissions: [Permissions.PERFORMANCE],
      filters: {
        hide: ["granularity"],
      },
    },
  },
  ...accountHealthRoutes,
  {
    path: "/customer-analytics",
    name: RouteNames.CUSTOMER_ANALYTICS,
    component: () => import("@/pages/CustomerAnalytics.vue"),
    meta: {
      title: "Customer Analytics",
      menuIcon: "lucide:users",
      permissions: [Permissions.CUSTOMER_ANALYTICS],
      filters: {
        show: ["channels", "dateRange", "granularity:onlyMonth", "brands", "products", "comparison"],
      },
    },
  },
  {
    path: "/profit-analysis",
    name: RouteNames.PROFIT_ANALYSIS,
    component: () => import("@/pages/ProfitAnalysis.vue"),
    meta: {
      title: "Profit & Margin Analysis",
      menuTitle: "Profit Analysis",
      menuIcon: "lucide:chart-pie",
      permissions: [Permissions.PROFIT_ANALYSIS],
      filters: {
        show: ["brands", "granularity", "dateRange"],
      },
    },
  },
  {
    path: "/business-review",
    name: RouteNames.BUSINESS_REVIEW,
    component: () => import("@/pages/BusinessReview.vue"),
    meta: {
      title: "Business Review",
      menuIcon: "lucide:clipboard-list",
      permissions: [Permissions.BUSINESS_REVIEW],
      filters: {
        // Week only — the backend accepts `granularity: "WEEK"` and nothing
        // else yet. Widen to "withoutDay" once monthly lands.
        show: ["brands", "channels", "dateRange", "granularity:onlyWeek"],
      },
    },
  },
  {
    path: "/warehouse",
    name: RouteNames.WAREHOUSE,
    meta: {
      permissions: [Permissions.WAREHOUSE],
      title: "Warehouse Management",
      menuTitle: "Warehouse",
      menuIcon: "lucide:warehouse",
      filters: {
        hide: ["channels"],
      },
    },
    component: () => import("@/pages/WarehousePage.vue"),
  },
  ...adminRoutes,
];

export default coreRoutes;
